/**
 * OpenAI Ads Measurement Pixel (oaiq).
 *
 * Dokumentation:
 * - https://learn.chatgpt.com/ads/measurement-pixel
 * - https://learn.chatgpt.com/ads/conversions-api
 *
 * Das SDK wird wie die übrigen Tracker deferred geladen (idle/Interaction).
 * Das Conversion-Event feuert ausschließlich nach bestätigter Speicherung
 * einer Anfrage und trägt dieselbe event_id wie das Serverevent (Deduplizierung).
 */

import { OAIQ_PIXEL_ID } from "@/lib/oaiq-config";

export { OAIQ_PIXEL_ID };

declare global {
  interface Window {
    oaiq?: ((...args: unknown[]) => void) & { q?: unknown[] };
  }
}

let initialized = false;

/** Lädt das SDK und initialisiert das Pixel. Auf jeder Seite einmal aufrufen. */
export function initOaiq(): void {
  if (initialized || typeof window === "undefined" || typeof document === "undefined") {
    return;
  }
  if (!window.oaiq) {
    const q = function (...args: unknown[]) {
      (q.q as unknown[]).push(args);
    } as ((...args: unknown[]) => void) & { q: unknown[] };
    q.q = [];
    window.oaiq = q;
    const script = document.createElement("script");
    script.async = true;
    script.src = "https://bzrcdn.openai.com/sdk/oaiq.min.js";
    const first = document.getElementsByTagName("script")[0];
    first?.parentNode?.insertBefore(script, first);
  }
  window.oaiq("init", { pixelId: OAIQ_PIXEL_ID, debug: import.meta.env.DEV });
  initialized = true;
}

/** Attributionswerte, die das SDK selbst setzt. Keine personenbezogenen Daten. */
export type OaiqAttribution = {
  /** Klick-Kennung aus Landingpage-URL bzw. First-Party-Cookie __oppref. */
  oppref?: string;
  /** Opaker Browser-Verweis aus dem First-Party-Cookie __obref. */
  obref?: string;
};

/** Nur opake, unverfängliche Kennungen zulassen (keine freien Strings/PII). */
const OPAQUE = /^[A-Za-z0-9._~:-]{1,200}$/;

function readCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
  if (!match?.[1]) return undefined;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

/**
 * Liest die vorhandene Attribution aus URL bzw. den vom SDK gesetzten
 * First-Party-Cookies. Es wird kein eigenes Tracking-Cookie gesetzt.
 */
export function readOaiqAttribution(): OaiqAttribution {
  if (typeof window === "undefined") return {};
  const out: OaiqAttribution = {};
  try {
    const fromUrl = new URLSearchParams(window.location.search).get("oppref") ?? undefined;
    const oppref = fromUrl ?? readCookie("__oppref");
    if (oppref && OPAQUE.test(oppref)) out.oppref = oppref;
    const obref = readCookie("__obref");
    if (obref && OPAQUE.test(obref)) out.obref = obref;
  } catch {
    /* ignore */
  }
  return out;
}

/**
 * Dokumentiertes Conversion-Event. Erst nach bestätigter Speicherung aufrufen.
 * `eventId` muss identisch mit der serverseitig gesendeten events[].id sein.
 */
export function trackOaiqLead(eventId: string): void {
  if (typeof window === "undefined" || !eventId) return;
  initOaiq();
  window.oaiq?.("measure", "lead_created", { type: "customer_action" }, { event_id: eventId });
}
